import { store } from 'createStore';
import type { SimulationResultInterface } from 'store/simulation/types';
import type { UnitInfoDataInterface } from 'store/unitInfoData';
import { conversionData, CONVERSIONS, getMetricUnit } from 'util/conversions';
import type {
  PortOutputFlowInterface,
  UnitInfoCrusherInterface,
  UnitInfoCrusherVSIInterface,
  UnitInfoGenericCrusherInterface,
  UnitInfoGyratoryCrusherCGInterface,
  UnitInfoHybridCrusherInterface,
  UnitInfoImpactCrusherCIInterface,
  UnitInfoJawCrusherInterface,
  UnitInfoStorageInterface,
  UnitInterface,
} from '../../store/units/types';
import { addNotesTexts } from './notesTexts';
import type { UnitInfoTextInterface } from './types';
import {
  addValue,
  attachmentTool,
  css,
  customModel,
  description,
  getMetricType,
  isImperial,
  liveBinDisplay,
  loadDisplay,
  modelName,
  productSize,
  tipSpeed,
  toMetricCapacity,
  toMetricMM,
  toMetricRoundConv,
  unitName,
} from './utils';

export const getGenericCrusherTexts = (
  unit: UnitInterface,
): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoGenericCrusherInterface;
  const textArray: UnitInfoTextInterface[] = [];

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);
  description(unitInfo, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getCrusherTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
  outputFlow?: PortOutputFlowInterface,
): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoCrusherInterface;
  const unitData = unitInfoData.PAN_FEEDER_SP;
  const load = simulationResult?.simulationUnitInfo?.load;
  const liveBin = simulationResult?.simulationUnitInfo?.liveBinVolume;
  const binFeederUsed = simulationResult?.simulationUnitInfo?.binFeederUsed;
  const autoSelectFeederRight =
    simulationResult?.simulationUnitInfo?.feederModelRight;

  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const {
    displayModelName,
    displayConcave,
    displayMantle,
    displayThrow,
    displayBushing,
  } = unitInfo.display;

  unitName(unit.type, unitInfo, textArray);

  let modelText = '';
  if (displayModelName) {
    modelText = unitInfo.modelName + ' ';
  }
  if (displayConcave) {
    modelText = modelText + unitInfo.concave + ' ';
  }
  if (displayMantle) {
    modelText = modelText + '(' + unitInfo.mantle + ') ';
  }
  if (binFeederUsed === true) {
    modelText = modelText + ' - Direct Feed';
  }

  customModel(unitInfo, modelText, textArray);
  add(`Throw: ${unitInfo.unitThrow}`, displayThrow);
  add(`Bushing: ${unitInfo.bushing}`, displayBushing);
  css(unitInfo, textArray);
  liveBinDisplay(unitInfo, liveBin, textArray);
  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  if (unitInfo.highBin.useHighBin) {
    if (unitInfo.display.displayFeederModel) {
      if (unitInfo.highBin.otherModelRight) {
        if (unitInfo.highBin.otherRightPanFeederModelName) {
          textArray.push({
            text: `Feeder: ${unitInfo.highBin.otherRightPanFeederModelName}`,
          });
        }
      } else
        textArray.push({
          text:
            unitInfo.highBin.autoSelect &&
            autoSelectFeederRight !== undefined &&
            autoSelectFeederRight !== unitInfo.highBin.rightPanFeederModelName // TODO: IS THIS CORRECT?
              ? `Feeder: ${unitData[autoSelectFeederRight]?.descriptiveName}`
              : `Feeder: ${
                  unitData[unitInfo.highBin.rightPanFeederModelName]
                    ?.descriptiveName
                }`,
        });
    }

    if (unitInfo.display.displayFeederInclination) {
      textArray.push({
        text: `Feeder inclination: ${unitInfo.highBin.inclinationRight} deg`,
      });
    }

    if (
      unitInfo.display.displayFeederLoad &&
      store.getState().settingsState?.unitDisplay?.showLoad
    ) {
      const feederLoadAverage = simulationResult?.simulationUnitInfo?.loadRight;
      const feederLoadMax = simulationResult?.simulationUnitInfo?.loadRightMax;
      textArray.push({
        text: `Feeder average load: ${feederLoadAverage ?? '-'} %`,
      });
      textArray.push({
        text: `Feeder max load: ${feederLoadMax ?? '-'} %`,
      });
    }

    if (unitInfo.highBin.displayProductSize && outputFlow) {
      const highBinStorageUnitInfo = {
        display: {
          displayProductSize: true,
          displayPredictedOversize: false,
        },
      } as UnitInfoStorageInterface;

      productSize(
        highBinStorageUnitInfo,
        outputFlow,
        textArray,
        'Bin material size: ',
      );
    }
  }

  return textArray;
};

export const getImpactCrusherCITexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitInfo = unit.unitInfo as UnitInfoImpactCrusherCIInterface;
  const { displaySetting, displayMotorPower } = unitInfo.display;
  const load = simulationResult?.simulationUnitInfo?.load;
  const setting = toMetricRoundConv('Setting:', unitInfo.setting);
  const motorPower = isImperial()
    ? `Installed motorpower: ${(
        unitInfo.motorPower * conversionData[CONVERSIONS.POWER].factor
      ).toFixed()} hp, ${unitInfo.motorFrequency} Hz`
    : `Installed motorpower: ${unitInfo.motorPower}kW, ${unitInfo.motorFrequency} Hz`;

  unitName(unit.type, unitInfo, textArray);

  if (unitInfo.display.displayModelName) {
    customModel(unitInfo, unitInfo.modelName, textArray);
  }

  tipSpeed(unitInfo, textArray);
  add(setting, displaySetting);
  add(motorPower, displayMotorPower);
  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getJawCrusherTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoJawCrusherInterface;
  const load = simulationResult?.simulationUnitInfo?.load;
  const textArray: UnitInfoTextInterface[] = [];

  unitName(unit.type, unitInfo, textArray);

  if (unitInfo.display.displayModelName) {
    customModel(unitInfo, unitInfo.modelName, textArray);
  }
  css(unitInfo, textArray);

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  if (
    unitInfo.attachmentTool.enabled &&
    unitInfo.display.displayAttachmentTool
  ) {
    addValue(textArray)('', true);
    addValue(textArray)('Attachment tool', true);
    attachmentTool(unit.type, unitInfo.attachmentTool, textArray, true);
  }

  return textArray;
};

export const getCrusherVSITexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
) => {
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitInfo = unit.unitInfo as UnitInfoCrusherVSIInterface;
  const liveBin = simulationResult?.simulationUnitInfo?.liveBinVolume;
  const rotorLoad = simulationResult?.simulationUnitInfo?.rotorLoad;
  const rotorCapacity = simulationResult?.simulationUnitInfo?.rotorCapacity;
  const biFlow = simulationResult?.simulationUnitInfo?.biFlow;
  const biFlowTonnage = simulationResult?.simulationUnitInfo?.biFlowTonnage;
  const { displayLoad, displayInstalledMotorPower, displayModelName } =
    unitInfo.display;

  unitName(unit.type, unitInfo, textArray);

  if (displayModelName) {
    customModel(unitInfo, unitInfo.modelName, textArray);
  }

  add(
    isImperial()
      ? `Motor Power: ${(
          unitInfo.motorPower * conversionData[CONVERSIONS.POWER].factor
        ).toFixed()} hp`
      : `Motor Power: ${unitInfo.motorPower} kW`,
    displayInstalledMotorPower,
  );
  tipSpeed(unitInfo, textArray);
  liveBinDisplay(unitInfo, liveBin, textArray);
  add(
    toMetricCapacity(`Biflow: ${biFlow}%`, biFlowTonnage),
    displayLoad && !!biFlowTonnage && !!biFlow,
  );
  add(
    toMetricCapacity(`Rotor Load: ${rotorLoad}%`, rotorCapacity),
    displayLoad && !!rotorLoad && !!rotorCapacity,
  );
  addNotesTexts({ unitInfo, textArray });
  return textArray;
};

export const getGyratoryCrusherCGTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoGyratoryCrusherCGInterface;
  const load = simulationResult?.simulationUnitInfo?.load;
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const {
    displayUnitThrow,
    displayModelName,
    displaySeparationSize,
    displayOss,
  } = unitInfo.display;

  unitName(unit.type, unitInfo, textArray);

  if (displayModelName) {
    customModel(unitInfo, unitInfo.modelName, textArray);
  }

  add(
    toMetricMM('Grizzly Separation:', unitInfo.decks[0].separationSize),
    displaySeparationSize && unitInfo.grizzly,
  );

  add(`Throw: ${unitInfo.unitThrow}`, displayUnitThrow);
  add(toMetricRoundConv('OSS:', unitInfo.oss), displayOss);

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  if (
    unitInfo.attachmentTool.enabled &&
    unitInfo.display.displayAttachmentTool
  ) {
    add('', true);
    add('Attachment tool', true);
    attachmentTool(unit.type, unitInfo.attachmentTool, textArray, true);
  }

  return textArray;
};

export const getHybridCrusherTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
): UnitInfoTextInterface[] => {
  const unitData = unitInfoData.HYBRID_CRUSHER_CR;
  const unitInfo = unit.unitInfo as UnitInfoHybridCrusherInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const mm = `[${getMetricUnit(getMetricType(), CONVERSIONS.MILLIMETER)}]`;
  const {
    displayModelName,
    displayRollDiameter,
    displayRollWidth,
    displaySegment,
    displayGapSetting,
    displayCompStrength,
  } = unitInfo.display;

  unitName(unit.type, unitInfo, textArray);

  const modelName = unitData.configurations?.rollDiameters[
    unitInfo.rollDiameter
  ].rollWidths[unitInfo.rollWidth]?.inOffering
    ? `${unitInfo.modelName}`
    : `${unitInfo.modelName} (N/A)`;

  add(modelName, displayModelName, true);
  add(`Roll diameter: ${unitInfo.rollDiameter} ${mm}`, displayRollDiameter);
  add(`Roll width: ${unitInfo.rollWidth} ${mm}`, displayRollWidth);
  add(`Segment: ${unitInfo.segment}`, displaySegment);
  add(`Adapted gap setting: ${unitInfo.gapSetting} ${mm}`, displayGapSetting);
  add(
    `Compressive strength: ${unitInfo.compressiveStrength}`,
    displayCompStrength,
  );
  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};
